import React, { useState } from 'react'
import {
  Plus,
  Edit2,
  Trash2,
  Check,
  X,
  Loader2,
  AlertTriangle,
  Mail,
  Server,
  Lock,
  TestTube,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import { useEmailAccounts, useEmailAccountMutations, EmailAccount, CreateEmailAccountInput } from '@/lib/data/hooks/useEmailAccounts'
import { useAdminUsers, AdminUser } from '@/lib/data/hooks/useAdminUsers'

interface EmailFormData {
  adminUserId: string
  emailAddress: string
  displayName: string

  // IMAP
  imapHost: string
  imapPort: string
  imapUsername: string
  imapPassword: string
  imapUseSsl: boolean

  // SMTP
  smtpHost: string
  smtpPort: string
  smtpUsername: string
  smtpPassword: string
  smtpUseTls: boolean
}

// DreamHost email server defaults
const initialFormData: EmailFormData = {
  adminUserId: '',
  emailAddress: '',
  displayName: '',
  imapHost: 'imap.dreamhost.com',
  imapPort: '993',
  imapUsername: '',
  imapPassword: '',
  imapUseSsl: true,
  smtpHost: 'smtp.dreamhost.com',
  smtpPort: '587',
  smtpUsername: '',
  smtpPassword: '',
  smtpUseTls: true,
}

export function EmailAccountsManagement() {
  const { accounts, isLoading, error: fetchError, refetch } = useEmailAccounts()
  const { users } = useAdminUsers()
  const { createAccount, updateAccount, deleteAccount, testConnection, isLoading: isMutating, error: mutationError } = useEmailAccountMutations()

  const [showAddModal, setShowAddModal] = useState(false)
  const [editingAccount, setEditingAccount] = useState<EmailAccount | null>(null)
  const [formData, setFormData] = useState<EmailFormData>(initialFormData)
  const [formError, setFormError] = useState<string | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [expandedAccount, setExpandedAccount] = useState<string | null>(null)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)
  const [isTesting, setIsTesting] = useState(false)

  const error = fetchError || mutationError || formError

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    const checked = (e.target as HTMLInputElement).checked
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
    setFormError(null)
    setTestResult(null)
  }

  const handleTestConnection = async () => {
    if (!formData.imapHost || !formData.smtpHost) {
      setFormError('Please provide both IMAP and SMTP hosts')
      return
    }

    setIsTesting(true)
    setTestResult(null)

    const result = await testConnection({
      adminUserId: formData.adminUserId,
      emailAddress: formData.emailAddress,
      imapHost: formData.imapHost,
      imapPort: parseInt(formData.imapPort) || 993,
      imapUsername: formData.imapUsername,
      imapPassword: formData.imapPassword,
      imapUseSsl: formData.imapUseSsl,
      smtpHost: formData.smtpHost,
      smtpPort: parseInt(formData.smtpPort) || 587,
      smtpUsername: formData.smtpUsername,
      smtpPassword: formData.smtpPassword,
      smtpUseTls: formData.smtpUseTls,
    })

    setTestResult(result)
    setIsTesting(false)
  }

  const handleSave = async () => {
    // Validation
    if (!formData.adminUserId) {
      setFormError('Please select a user')
      return
    }
    if (!formData.emailAddress) {
      setFormError('Email address is required')
      return
    }
    if (!formData.imapHost || !formData.smtpHost) {
      setFormError('IMAP and SMTP hosts are required')
      return
    }

    const input: CreateEmailAccountInput = {
      adminUserId: formData.adminUserId,
      emailAddress: formData.emailAddress,
      displayName: formData.displayName || undefined,
      imapHost: formData.imapHost,
      imapPort: parseInt(formData.imapPort) || 993,
      imapUsername: formData.imapUsername || formData.emailAddress,
      imapPassword: formData.imapPassword,
      imapUseSsl: formData.imapUseSsl,
      smtpHost: formData.smtpHost,
      smtpPort: parseInt(formData.smtpPort) || 587,
      smtpUsername: formData.smtpUsername || formData.emailAddress,
      smtpPassword: formData.smtpPassword,
      smtpUseTls: formData.smtpUseTls,
    }

    if (editingAccount) {
      const result = await updateAccount(editingAccount.id, {
        emailAddress: formData.emailAddress,
        displayName: formData.displayName || undefined,
        imapHost: formData.imapHost,
        imapPort: parseInt(formData.imapPort) || 993,
        imapUsername: formData.imapUsername || formData.emailAddress,
        imapPassword: formData.imapPassword,
        imapUseSsl: formData.imapUseSsl,
        smtpHost: formData.smtpHost,
        smtpPort: parseInt(formData.smtpPort) || 587,
        smtpUsername: formData.smtpUsername || formData.emailAddress,
        smtpPassword: formData.smtpPassword,
        smtpUseTls: formData.smtpUseTls,
      })
      if (result) {
        handleCloseModal()
        refetch()
      }
    } else {
      const result = await createAccount(input)
      if (result) {
        handleCloseModal()
        refetch()
      }
    }
  }

  const handleEdit = (account: EmailAccount) => {
    setEditingAccount(account)
    setFormData({
      adminUserId: account.adminUserId,
      emailAddress: account.emailAddress,
      displayName: account.displayName || '',
      imapHost: account.imapHost,
      imapPort: account.imapPort.toString(),
      imapUsername: account.imapUsername,
      imapPassword: '', // Don't pre-fill password
      imapUseSsl: account.imapUseSsl,
      smtpHost: account.smtpHost,
      smtpPort: account.smtpPort.toString(),
      smtpUsername: account.smtpUsername,
      smtpPassword: '', // Don't pre-fill password
      smtpUseTls: account.smtpUseTls,
    })
    setShowAddModal(true)
  }

  const handleDelete = async (accountId: string) => {
    const success = await deleteAccount(accountId)
    if (success) {
      setDeleteConfirm(null)
      refetch()
    }
  }

  const handleCloseModal = () => {
    setShowAddModal(false)
    setEditingAccount(null)
    setFormData(initialFormData)
    setFormError(null)
    setTestResult(null)
  }

  const getUserById = (userId: string): AdminUser | undefined => {
    return users.find(u => u.id === userId)
  }

  // Group accounts by user
  const accountsByUser = accounts.reduce((acc, account) => {
    const userId = account.adminUserId
    if (!acc[userId]) {
      acc[userId] = []
    }
    acc[userId].push(account)
    return acc
  }, {} as Record<string, EmailAccount[]>)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-[var(--text-main)]">Email Accounts</h3>
          <p className="text-[var(--text-muted)] text-sm mt-1">
            Configure IMAP/SMTP email accounts for team members
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          disabled={users.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
        >
          <Plus size={16} />
          Add Email Account
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
          <AlertTriangle size={16} />
          {error}
        </div>
      )}

      {/* Accounts List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
          </div>
        ) : accounts.length === 0 ? (
          <div className="bg-[var(--bg-element)] border border-[var(--border)] rounded-xl text-center py-12 text-[var(--text-muted)]">
            <Mail size={48} className="mx-auto mb-4 opacity-50" />
            <p>No email accounts configured</p>
            <p className="text-sm mt-1">Add email accounts for team members to use webmail</p>
          </div>
        ) : (
          Object.entries(accountsByUser).map(([userId, userAccounts]) => {
            const user = getUserById(userId)
            return (
              <div key={userId} className="bg-[var(--bg-element)] border border-[var(--border)] rounded-xl overflow-hidden">
                {/* User Header */}
                <div className="px-4 py-3 bg-[var(--bg-card)] border-b border-[var(--border)] flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-[var(--primary)] to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                    {user?.firstName?.[0] || user?.email[0].toUpperCase() || '?'}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[var(--text-main)]">
                      {user?.firstName && user?.lastName
                        ? `${user.firstName} ${user.lastName}`
                        : user?.email || 'Unknown User'}
                    </p>
                    <p className="text-xs text-[var(--text-muted)]">
                      {userAccounts.length} email account{userAccounts.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>

                {/* Email Accounts */}
                <div className="divide-y divide-[var(--border)]">
                  {userAccounts.map(account => (
                    <div key={account.id}>
                      <div
                        className="px-4 py-3 flex items-center justify-between hover:bg-[var(--bg-hover)] cursor-pointer transition-colors"
                        onClick={() => setExpandedAccount(expandedAccount === account.id ? null : account.id)}
                      >
                        <div className="flex items-center gap-3">
                          <Mail size={16} className="text-[var(--text-muted)]" />
                          <div>
                            <p className="text-sm font-medium text-[var(--text-main)]">
                              {account.emailAddress}
                            </p>
                            {account.displayName && (
                              <p className="text-xs text-[var(--text-muted)]">{account.displayName}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            account.isActive
                              ? 'bg-emerald-500/10 text-emerald-400'
                              : 'bg-[var(--bg-card)] text-[var(--text-muted)]'
                          }`}>
                            {account.isActive ? 'Active' : 'Inactive'}
                          </span>
                          {expandedAccount === account.id ? (
                            <ChevronUp size={16} className="text-[var(--text-muted)]" />
                          ) : (
                            <ChevronDown size={16} className="text-[var(--text-muted)]" />
                          )}
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {expandedAccount === account.id && (
                        <div className="px-4 py-3 bg-[var(--bg-card)] border-t border-[var(--border)]">
                          <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                            <div>
                              <p className="text-xs text-[var(--text-muted)] uppercase tracking-wider mb-1">IMAP Server</p>
                              <p className="text-[var(--text-main)]">
                                {account.imapHost}:{account.imapPort}
                                {account.imapUseSsl && ' (SSL)'}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-[var(--text-muted)] uppercase tracking-wider mb-1">SMTP Server</p>
                              <p className="text-[var(--text-main)]">
                                {account.smtpHost}:{account.smtpPort}
                                {account.smtpUseTls && ' (TLS)'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleEdit(account)
                              }}
                              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-[var(--bg-element)] text-[var(--text-muted)] hover:text-[var(--text-main)] rounded-lg transition-colors"
                            >
                              <Edit2 size={14} />
                              Edit
                            </button>
                            {deleteConfirm === account.id ? (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleDelete(account.id)
                                  }}
                                  disabled={isMutating}
                                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                                >
                                  <Check size={14} />
                                  Confirm
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setDeleteConfirm(null)
                                  }}
                                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-[var(--bg-element)] text-[var(--text-muted)] hover:text-[var(--text-main)] rounded-lg transition-colors"
                                >
                                  <X size={14} />
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setDeleteConfirm(account.id)
                                }}
                                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-[var(--bg-element)] text-[var(--text-muted)] hover:text-red-400 rounded-lg transition-colors"
                              >
                                <Trash2 size={14} />
                                Delete
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[110] p-4">
          <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[var(--border)]">
              <h2 className="text-xl font-bold text-[var(--text-main)]">
                {editingAccount ? 'Edit Email Account' : 'Add Email Account'}
              </h2>
              <p className="text-[var(--text-muted)] text-sm mt-1">
                Configure IMAP/SMTP settings for email access
              </p>
            </div>

            <div className="p-6 space-y-4">
              {formError && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                  <AlertTriangle size={16} />
                  {formError}
                </div>
              )}

              {/* User Selection */}
              <div>
                <label className="block text-sm font-medium text-[var(--text-main)] mb-2">
                  Assign to User
                </label>
                <select
                  name="adminUserId"
                  value={formData.adminUserId}
                  onChange={handleInputChange}
                  disabled={!!editingAccount}
                  className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)] disabled:opacity-50"
                >
                  <option value="">Select a user...</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.firstName && user.lastName
                        ? `${user.firstName} ${user.lastName} (${user.email})`
                        : user.email}
                    </option>
                  ))}
                </select>
              </div>

              {/* Email Address */}
              <div>
                <label className="block text-sm font-medium text-[var(--text-main)] mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  name="emailAddress"
                  value={formData.emailAddress}
                  onChange={handleInputChange}
                  placeholder="user@example.com"
                  className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
                />
              </div>

              {/* Display Name */}
              <div>
                <label className="block text-sm font-medium text-[var(--text-main)] mb-2">
                  Display Name <span className="text-[var(--text-muted)]">(optional)</span>
                </label>
                <input
                  type="text"
                  name="displayName"
                  value={formData.displayName}
                  onChange={handleInputChange}
                  placeholder="Work Email"
                  className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-4 py-2 rounded-lg focus:outline-none focus:border-[var(--primary)]"
                />
              </div>

              {/* IMAP Settings */}
              <div className="pt-4 border-t border-[var(--border)]">
                <h3 className="text-sm font-semibold text-[var(--text-main)] mb-4 flex items-center gap-2">
                  <Server size={16} />
                  IMAP Settings (Incoming)
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label className="block text-xs text-[var(--text-muted)] mb-1">Host</label>
                    <input
                      type="text"
                      name="imapHost"
                      value={formData.imapHost}
                      onChange={handleInputChange}
                      placeholder="imap.dreamhost.com"
                      className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-3 py-2 rounded-lg text-sm focus:outline-none focus:border-[var(--primary)]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[var(--text-muted)] mb-1">Port</label>
                    <input
                      type="text"
                      name="imapPort"
                      value={formData.imapPort}
                      onChange={handleInputChange}
                      placeholder="993"
                      className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-3 py-2 rounded-lg text-sm focus:outline-none focus:border-[var(--primary)]"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <label className="block text-xs text-[var(--text-muted)] mb-1">Username</label>
                    <input
                      type="text"
                      name="imapUsername"
                      value={formData.imapUsername}
                      onChange={handleInputChange}
                      placeholder="Same as email"
                      className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-3 py-2 rounded-lg text-sm focus:outline-none focus:border-[var(--primary)]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[var(--text-muted)] mb-1">Password</label>
                    <input
                      type="password"
                      name="imapPassword"
                      value={formData.imapPassword}
                      onChange={handleInputChange}
                      placeholder="Email password"
                      className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-3 py-2 rounded-lg text-sm focus:outline-none focus:border-[var(--primary)]"
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2 mt-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="imapUseSsl"
                    checked={formData.imapUseSsl}
                    onChange={handleInputChange}
                    className="w-4 h-4 rounded border-[var(--border)] text-[var(--primary)] focus:ring-[var(--primary)]"
                  />
                  <span className="text-sm text-[var(--text-main)]">Use SSL/TLS</span>
                </label>
              </div>

              {/* SMTP Settings */}
              <div className="pt-4 border-t border-[var(--border)]">
                <h3 className="text-sm font-semibold text-[var(--text-main)] mb-4 flex items-center gap-2">
                  <Lock size={16} />
                  SMTP Settings (Outgoing)
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label className="block text-xs text-[var(--text-muted)] mb-1">Host</label>
                    <input
                      type="text"
                      name="smtpHost"
                      value={formData.smtpHost}
                      onChange={handleInputChange}
                      placeholder="smtp.dreamhost.com"
                      className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-3 py-2 rounded-lg text-sm focus:outline-none focus:border-[var(--primary)]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[var(--text-muted)] mb-1">Port</label>
                    <input
                      type="text"
                      name="smtpPort"
                      value={formData.smtpPort}
                      onChange={handleInputChange}
                      placeholder="587"
                      className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-3 py-2 rounded-lg text-sm focus:outline-none focus:border-[var(--primary)]"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <label className="block text-xs text-[var(--text-muted)] mb-1">Username</label>
                    <input
                      type="text"
                      name="smtpUsername"
                      value={formData.smtpUsername}
                      onChange={handleInputChange}
                      placeholder="Same as email"
                      className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-3 py-2 rounded-lg text-sm focus:outline-none focus:border-[var(--primary)]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[var(--text-muted)] mb-1">Password</label>
                    <input
                      type="password"
                      name="smtpPassword"
                      value={formData.smtpPassword}
                      onChange={handleInputChange}
                      placeholder="Email password"
                      className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] px-3 py-2 rounded-lg text-sm focus:outline-none focus:border-[var(--primary)]"
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2 mt-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="smtpUseTls"
                    checked={formData.smtpUseTls}
                    onChange={handleInputChange}
                    className="w-4 h-4 rounded border-[var(--border)] text-[var(--primary)] focus:ring-[var(--primary)]"
                  />
                  <span className="text-sm text-[var(--text-main)]">Use STARTTLS</span>
                </label>
              </div>

              {/* Test Connection */}
              <div className="pt-4 border-t border-[var(--border)]">
                <button
                  onClick={handleTestConnection}
                  disabled={isTesting || !formData.imapHost || !formData.smtpHost}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[var(--bg-element)] text-[var(--text-main)] rounded-lg hover:bg-[var(--bg-hover)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isTesting ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <TestTube size={16} />
                  )}
                  Test Connection
                </button>

                {testResult && (
                  <div className={`mt-3 flex items-center gap-2 p-3 rounded-lg text-sm ${
                    testResult.success
                      ? 'bg-emerald-500/10 text-emerald-400'
                      : 'bg-red-500/10 text-red-400'
                  }`}>
                    {testResult.success ? <Check size={16} /> : <X size={16} />}
                    {testResult.message}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Actions */}
            <div className="p-6 border-t border-[var(--border)] flex items-center justify-end gap-3">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isMutating}
                className="flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isMutating ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check size={16} />
                    {editingAccount ? 'Update Account' : 'Add Account'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
